import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Skeleton
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  gradient?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
  onClick?: () => void;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  gradient,
  trend,
  loading = false,
  onClick
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card
        sx={{
          height: '100%',
          background: gradient || theme.palette.background.paper,
          borderRadius: theme.general.borderRadius
        }}
      >
        <CardContent>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={48} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        background: gradient || theme.palette.background.paper,
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: onClick ? 'translateY(-4px)' : 'none',
          boxShadow: onClick ? theme.colors.shadows.cardLg : theme.colors.shadows.card
        }
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: gradient
                  ? theme.colors.alpha.white[70]
                  : theme.palette.text.secondary,
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.5px'
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: gradient
                  ? theme.colors.alpha.white[100]
                  : theme.palette.text.primary,
                fontWeight: 700,
                mt: 1,
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '12px',
              background: gradient
                ? theme.colors.alpha.white[10]
                : theme.colors.alpha.black[5],
              color: gradient
                ? theme.colors.alpha.white[100]
                : theme.palette.primary.main
            }}
          >
            {icon}
          </Box>
        </Box>
        {trend && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            {trend.isPositive ? (
              <TrendingUpIcon
                sx={{
                  fontSize: '1rem',
                  color: gradient
                    ? theme.colors.alpha.white[100]
                    : theme.palette.success.main
                }}
              />
            ) : (
              <TrendingDownIcon
                sx={{
                  fontSize: '1rem',
                  color: gradient
                    ? theme.colors.alpha.white[100]
                    : theme.palette.error.main
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                color: gradient
                  ? theme.colors.alpha.white[70]
                  : theme.palette.text.secondary,
                fontWeight: 600
              }}
            >
              {trend.value}% from last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};